import { Form, FormInstance, Input } from 'antd';
import { useTranslation } from 'react-i18next';

import { ResultCode } from '@/infra/adapters/models';
import { InvalidParamError } from '@/infra/exceptions';
import { AuthService } from '@/v1/main/domain/auth';
import { MainForm } from '@/v1/main/views/Form';
import {
    MessageOption,
    MessageType,
    useAuth,
    useMessage,
} from '@/v1/main/views/Provider';

interface FormProps {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    usecase: AuthService;
}

export const ChangePasswordForm: React.FC<Props> = ({
    open,
    onClose,
    usecase,
}: Props) => {
    const { t } = useTranslation(['interface', 'title', 'field']);
    const { setLoggedIn } = useAuth();
    const { setMessageOption } = useMessage();
    const initialValues = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    };

    const onSubmit = async ({ oldPassword, newPassword }: FormProps) => {
        await usecase.changePassword(oldPassword, newPassword);
        const opt: MessageOption = {
            type: MessageType.SUCCESS,
            content: t('message.change_password_redirect', { ns: 'interface' }),
        };
        setMessageOption(opt);
        setLoggedIn(false);
    };

    const onFail = (err: unknown, form: FormInstance) => {
        if (err instanceof InvalidParamError) {
            const fields = [];
            if (err.code == ResultCode.INVALID_OLD_PASSWORD) {
                fields.push({
                    name: 'oldPassword',
                    errors: [
                        t('validator.invalid', {
                            ns: 'interface',
                            field: t('old_password', { ns: 'field' }),
                        }),
                    ],
                });
            } else if (err.code == ResultCode.INVALID_NEW_PASSWORD) {
                fields.push({
                    name: 'newPassword',
                    errors: [
                        t('validator.invalid', {
                            ns: 'interface',
                            field: t('new_password', { ns: 'field' }),
                        }),
                    ],
                });
            }
            if (fields.length > 0) {
                form.setFields(fields);
                return Promise.resolve();
            }
        } else {
            const opt: MessageOption = {
                type: MessageType.ERROR,
                content: t('message.action_failed', {
                    ns: 'interface',
                    tip: '',
                }),
            };
            setMessageOption(opt);
        }
    };

    return (
        <MainForm<FormProps>
            name="change_password"
            title={t('action.change_password', { ns: 'interface' })}
            open={open}
            onClose={onClose}
            initialValues={initialValues}
            onSubmit={onSubmit}
            onFail={onFail}
        >
            <Form.Item
                name="oldPassword"
                label={t('old_password', { ns: 'field' })}
                validateFirst
                rules={[
                    {
                        required: true,
                        message: t<string>('validator.required', {
                            ns: 'interface',
                            field: '${label}',
                        }),
                    },
                    {
                        pattern: new RegExp(/^(?=\S).*(?:[^\s])$/, 'g'),
                        message: t<string>('validator.whitespace', {
                            ns: 'interface',
                        }),
                    },
                    {
                        pattern: new RegExp(
                            '^[a-zA-Z0-9\u0040\u0024\u0023\u0025\u005E\u0026\u002A]{8,32}$',
                            'g'
                        ),
                        message: t<string>('validator.invalid', {
                            ns: 'interface',
                            field: '${label}',
                        }),
                    },
                ]}
            >
                <Input.Password />
            </Form.Item>
            <Form.Item
                name="newPassword"
                label={t('new_password', { ns: 'field' })}
                validateFirst
                rules={[
                    {
                        required: true,
                        message: t<string>('validator.required', {
                            ns: 'interface',
                            field: '${label}',
                        }),
                    },
                    {
                        pattern: new RegExp(/^(?=\S).*(?:[^\s])$/, 'g'),
                        message: t<string>('validator.whitespace', {
                            ns: 'interface',
                        }),
                    },
                    {
                        pattern: new RegExp(
                            '^[a-zA-Z0-9\u0040\u0024\u0023\u0025\u005E\u0026\u002A]{8,32}$',
                            'g'
                        ),
                        message: t<string>('validator.password_invalid', {
                            ns: 'interface',
                            field: '${label}',
                        }),
                    },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (
                                !value ||
                                getFieldValue('oldPassword') !== value
                            ) {
                                return Promise.resolve();
                            }
                            const msg = t('validator.not_equal', {
                                ns: 'interface',
                                field: '${label}',
                                value: t('old_password', { ns: 'field' }),
                            });
                            return Promise.reject(new Error(msg));
                        },
                    }),
                ]}
            >
                <Input.Password />
            </Form.Item>
            <Form.Item
                name="confirmPassword"
                label={t('confirm_password', { ns: 'field' })}
                validateFirst
                rules={[
                    {
                        required: true,
                        message: t<string>('validator.required', {
                            ns: 'interface',
                            field: '${label}',
                        }),
                    },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (
                                !value ||
                                getFieldValue('newPassword') === value
                            ) {
                                return Promise.resolve();
                            }
                            const msg = t('validator.equal', {
                                ns: 'interface',
                                field: '${label}',
                                value: t('new_password', { ns: 'field' }),
                            });
                            return Promise.reject(new Error(msg));
                        },
                    }),
                ]}
            >
                <Input.Password />
            </Form.Item>
        </MainForm>
    );
};
